import React, { useContext, useRef, useMemo, useState, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { cloneDeep } from 'lodash-es';
import { useMemoizedFn } from 'ahooks';
import { Form } from 'tea-component';
import TinyEditor from '@gp/components/TinyEditor';
import { context as Context } from '@gp/components/Context';
import UploadFileList from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/UploadFileList';
import DateSelector from '../DateSelector';
import GRActionItems from './components/GRActionItems';
import ActionItems from './components/ActionItems';
import Risks from './components/Risks';
import useGetState from '@intelligence/hooks/useGetState';
import { fetchGRActionItems } from '@gp/services/projectUpdate';
import { addId, getValidDate, isNullOrUndefined } from '@gp/utils/common';
import {  UPLOAD_FOLDER_NAME_MAP } from '@gp/constants/const';
import {
  getIsDuplicateDate,
  getIsRisksValid,
  getIsWorkplansValid,
  getIsListNotEmpty,
  getProjectUpdateFormValidations,
  getProjectUpdateFormFieldValidation,
  formatGRActionItemsRes,
  getUpdatesDateValue,
  getGRActionItemsFormItemLabel,
} from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/utils';
import { synchronizeActionItems, composeActionItems, synchronizeRisks, composeRisks } from './utils';
import { DEFAULT_RISKS_ITEM, DEFAULT_WORKPLANS_ITEM } from './constants';
import { useAutoSaveParams } from '@gp/hooks/useAutoSaveParams';
import type { IBaseProps } from '@home/types/common';
import type { IRecord, IValidation, IMaterialVO } from '@gp/types/global';
import type { IRiskVO, IActionItemVO, IGRActionItemVO } from '@gp/types/projectUpdate';
import './index.scss';
import './form.scss';

interface IEditData {
  date: string;
  granularity: number; // 1：月，2：周
  update_time: string;
  update_user: string; // 名字要填当前人员的
  content: {
    key_updates: string | null;
    risks: IRiskVO[] | null;
    workplans: IActionItemVO[] | null;
  };
  gr_action_milestone_id: string;
  gr_action_milestone: string;
  gr_action: IGRActionItemVO[] | null;
  material: IMaterialVO[] | null;
}

interface IUpdateProjectEditFormProps extends IBaseProps {
  isEntryEdit: boolean;
  submitted?: boolean;
  editValid?: boolean;
  mode: 'add' | 'edit' | '';
  dataList: any[];
  data: IEditData | null;
  setEditValid?: React.Dispatch<React.SetStateAction<boolean>>;
  setEditAvailable?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IUpdateProjectEditFormInstance {
  getData: () => IEditData | null;
}

const KEY_UPDATES_PLACEHOLDER = `We appreciate detailed and insightful updates from you. Instead of simply saying, "We are following up with the new combat design," consider providing a more complete picture, including details like scope, budget, timeline, and your own insights.

For example, you could say something like, "The studio provided us with a new combat design last week, and we are spending this week reviewing it internally. We’ll discuss our feedback with the studio next week before presenting the final combat design to the executives on February 1st."`;

const UpdateProjectEditForm: React.ForwardRefRenderFunction<
IUpdateProjectEditFormInstance,
IUpdateProjectEditFormProps
> = (props, ref) => {
  const {
    style,
    className,
    isEntryEdit,
    submitted,
    editValid,
    mode,
    dataList,
    data,
    setEditValid,
    setEditAvailable,
    pageKeyCur,
    initialParams,
    setIsCompletedVideo,
    setDeletePathList,
    deletePathList,
    actionItemCategory,
  } = props;

  const { projectDetailData } = useContext<IRecord>(Context);
  const { project_id: projectId } = projectDetailData || {};
  // const hasChangedRef = useRef(false);
  const hasChangedKeyUpdataRef = useRef(false);
  const hasChangedMaterRef = useRef(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isChangeValue, setIsChangeValue] = useState<boolean>(false);
  const initDataRef = useRef<IEditData | null>(null);
  const [currentList, setCurrentList] = useState<IEditData | null>(null);
  // Key Updates纯文本内容，用来判断是否输入为空
  const [keyUpdatesText, setKeyUpdatesText] = useState<string>('');
  // material初始值
  const [initialFileDatas, setInitialFileDatas] = useState<IMaterialVO[]>([]);

  const {
    hasGRActionItems,
    grActionItems,
    grActionItemsMilestone,
    hasWorkplansData,
    workplansData,
    hasRisksData,
    risksData,
  } = useMemo(() => {
    const { content, gr_action: grActionItems, gr_action_milestone: grActionItemsMilestone } = currentList || {};
    const { workplans, risks } = content || {};

    return {
      hasGRActionItems: Array.isArray(grActionItems) && grActionItems.length > 0,
      grActionItems,
      grActionItemsMilestone,
      hasWorkplansData: Array.isArray(workplans) && workplans.length > 0,
      workplansData: workplans,
      hasRisksData: Array.isArray(risks) && risks.length > 0,
      risksData: risks,
    };
  }, [currentList]);

  // 新增、编辑的表单校验 报错信息
  const [editValidations, setEditValidations] = useState<IValidation[]>([]);
  // 新增、编辑的表单校验 报错信息 mapping映射
  const editValidationsMapping = useMemo<{
    dateDataFieldValidation: IValidation | null;
    keyUpdatesFieldValidation: IValidation | null;
    risksFieldValidation: IValidation | null;
    workplansFieldValidation: IValidation | null;
  }>(
    () => ({
      dateDataFieldValidation: getProjectUpdateFormFieldValidation('dateData', submitted, editValid, editValidations),
      keyUpdatesFieldValidation: getProjectUpdateFormFieldValidation(
        'keyUpdates',
        submitted,
        editValid,
        editValidations,
      ),
      risksFieldValidation: getProjectUpdateFormFieldValidation('risks', submitted, editValid, editValidations),
      workplansFieldValidation: getProjectUpdateFormFieldValidation('workplans', submitted, editValid, editValidations),
    }),
    [submitted, editValid, editValidations],
  );

  const reset = useMemoizedFn(() => {
    initDataRef.current = null;
    setCurrentList(null);
    setInitialFileDatas([]);
  });

  const handleAdd = useMemoizedFn((field: string, type: string) => {
    const newList = cloneDeep(currentList) as IEditData;
    const defaultItem: any = field === 'risks' ? addId(DEFAULT_RISKS_ITEM) : addId(DEFAULT_WORKPLANS_ITEM);
    // @ts-ignore
    newList.content[field] =
      type === 'init'
        ? [defaultItem]
        : [
          // @ts-ignore
          ...newList.content[field],
          defaultItem,
        ];
    setCurrentList(newList);
  });

  const handleDelete = useMemoizedFn((field: string, index: number) => {
    const newList = cloneDeep(currentList) as IEditData;
    // @ts-ignore
    (newList.content[field] as [])?.splice(index, 1);
    setCurrentList(newList);
  });

  // Title日期发生编辑
  const handleDateUpdate = useMemoizedFn((value: string | [string, string], type: string) => {
    setIsChangeValue(true);
    const isMonth = type === 'month';
    const newDate = isMonth
      ? getValidDate(value as string, 'YYYY.MM', '')
      : value?.[0] && value?.[1]
        ? `${getValidDate(value?.[0], 'YYYY.MM.DD', '')}-${getValidDate(value?.[1], 'YYYY.MM.DD', '')}`
        : '';
    const newList = cloneDeep(currentList) as IEditData;
    newList.date = newDate;
    newList.granularity = type === 'month' ? 1 : 2;
    setCurrentList(newList);

    const [startDate, endDate] = getUpdatesDateValue(type, value);
    if (!!startDate && !!endDate) {
      refreshGRActionItems(newList.granularity, newList.date, {
        project_id: isNullOrUndefined(projectId) ? '' : projectId,
        granularity: String(newList.granularity),
        start_date: startDate || '',
        end_date: endDate || '',
      });
    }
  });

  // Key Updates发生编辑
  const handleKeyUpdatesUpdate = useMemoizedFn((v: string) => {
    if (!v) return;
    if (hasChangedKeyUpdataRef.current) {
      setIsChangeValue(true);
    }
    hasChangedKeyUpdataRef.current = true;
    const newList = cloneDeep(currentList) as IEditData;
    newList.content.key_updates = v;
    setCurrentList(newList);
  });

  const [isRisksSynchronizing, setIsRisksSynchronizing, getIsRisksSynchronizing] = useGetState(false);

  // 继承Risks
  const handleRiskSynchronize = () => {
    if (getIsRisksSynchronizing()) return;
    setIsRisksSynchronizing(true);
    const { granularity, date } = currentList || {};
    const synchronizedRisks = synchronizeRisks({
      granularity,
      date,
      list: dataList,
    });
    const risks = composeRisks(currentList?.content?.risks, synchronizedRisks);
    const currentListTmp = cloneDeep(currentList) as IEditData;
    if (currentListTmp?.content) {
      currentListTmp.content.risks = risks;
    }
    setCurrentList(currentListTmp);
    // REMARK: 模拟数据加载（因为这里是前端计算，但是设计稿有加载动画，所以这里需要模拟下网络加载时长）
    setTimeout(() => {
      setIsRisksSynchronizing(false);
    }, 600);
  };

  // Risks发生编辑
  const handleRisksUpdate = useMemoizedFn((index: number, payload: IRecord) => {
    setIsChangeValue(true);
    const newList = cloneDeep(currentList) as IEditData;
    const newRisks = newList?.content?.risks;
    const editItem: any = newRisks?.[index] || {};
    const newItem = {
      ...editItem,
      ...payload,
    };
    newRisks?.splice(index, 1, newItem);
    newList.content.risks = newRisks;
    setCurrentList(newList);
  });

  // GR Action Items delete回调
  const handleGRActionItemsDelete = (index: number) => {
    if (!currentList) return;
    const currentListTmp = cloneDeep(currentList);
    const grActionItemsTmp = currentListTmp?.gr_action || [];
    const grActionItemsNew = [...grActionItemsTmp.slice(0, index), ...grActionItemsTmp.slice(index + 1)];
    currentListTmp.gr_action = grActionItemsNew;
    setCurrentList(currentListTmp);
  };

  // GR Action Items change回调
  const handleGRActionItemsUpdate = (index: number, payload: IRecord) => {
    setIsChangeValue(true);
    if (!currentList) return;
    const currentListTmp = cloneDeep(currentList);
    const grActionItemsTmp = currentListTmp?.gr_action || [];
    const grActionItemTmp = grActionItemsTmp?.[index] || {};

    const grActionItemNew = {
      ...grActionItemTmp,
      ...payload,
    };
    const grActionItemsNew = [
      ...grActionItemsTmp.slice(0, index),
      grActionItemNew,
      ...grActionItemsTmp.slice(index + 1),
    ];
    currentListTmp.gr_action = grActionItemsNew;
    setCurrentList(currentListTmp);
  };

  const [isWorkplansSynchronizing, setIsWorkplansSynchronizing, getIsWorkplansSynchronizing] = useGetState(false);

  // 继承Action Items数据
  const handleWorkplansSynchronize = () => {
    setIsChangeValue(true);
    if (getIsWorkplansSynchronizing()) return;
    setIsWorkplansSynchronizing(true);
    const { granularity, date } = currentList || {};
    const synchronizedActionItems = synchronizeActionItems({
      granularity,
      date,
      list: dataList,
    });
    const actionItems = composeActionItems(currentList?.content?.workplans, synchronizedActionItems);
    const currentListTmp = cloneDeep(currentList) as IEditData;
    if (currentListTmp?.content) {
      currentListTmp.content.workplans = actionItems;
    }
    setCurrentList(currentListTmp);
    // REMARK: 模拟数据加载（因为这里是前端计算，但是设计稿有加载动画，所以这里需要模拟下网络加载时长）
    setTimeout(() => {
      setIsWorkplansSynchronizing(false);
    }, 600);
  };

  // Action Items发生编辑
  const handleWorkplansUpdate = useMemoizedFn((index: number, payload: IRecord) => {
    setIsChangeValue(true);
    const newList = cloneDeep(currentList) as IEditData;
    const newWorkplans = newList?.content?.workplans;
    const editItem: any = newWorkplans?.[index] || {};
    const newItem = {
      ...editItem,
      ...payload,
    };
    newWorkplans?.splice(index, 1, newItem);
    newList.content.workplans = newWorkplans;
    setCurrentList(newList);
  });

  const handleMaterialUpdate = (payload?: IMaterialVO) => {
    if (!payload) return;
    if (hasChangedMaterRef.current) {
      setIsChangeValue(true);
    }
    hasChangedMaterRef.current = true;
    const newList = cloneDeep(currentList) as IEditData;
    newList.material = Array.isArray(payload) ? payload : [];
    setCurrentList(newList);
  };

  const getData = useMemoizedFn(() => {
    if (!currentList) return null;
    return cloneDeep(currentList);
  });

  // 进入弹窗时默认数据，弹窗消失时重置数据
  useEffect(() => {
    if (isEntryEdit) {
      initDataRef.current = data;
      // 深拷贝，避免影响initData
      const currentListTmp = cloneDeep(data);
      if (currentListTmp?.content) {
        if (!currentListTmp?.content?.workplans?.length) {
          currentListTmp.content.workplans = [{ ...addId(DEFAULT_WORKPLANS_ITEM) }];
        }
        if (!currentListTmp?.content?.risks?.length) {
          currentListTmp.content.risks = [{ ...addId(DEFAULT_RISKS_ITEM), rating: '' }];
        }
      }
      setCurrentList(currentListTmp);
      setInitialFileDatas(cloneDeep(data?.material || []));
      setIsReady(true);
    } else {
      reset();
    }
  }, [isEntryEdit]);

  useEffect(() => {
    const initialGranularity = initDataRef.current?.granularity;
    const initialdate = initDataRef.current?.date;
    const granularity = currentList?.granularity;
    const date = currentList?.date;
    const content = currentList?.content;
    const keyUpdates = keyUpdatesText?.trim();
    const risks = content?.risks;
    const workplans = content?.workplans;
    const isGranularityDateExisted = !!granularity && !!date;
    const isGranularityDateValidDuplicate =
      isGranularityDateExisted &&
      getIsDuplicateDate(mode, {
        projectUpdates: dataList,
        dateData: {
          granularity,
          date,
        },
        initialDateData: {
          granularity: initialGranularity,
          date: initialdate,
        },
      });
    const isGranularityDateValid = isGranularityDateExisted && !isGranularityDateValidDuplicate;
    const isGranularityDateAvailable = isGranularityDateExisted;
    const isKeyUpdatesValid = !!keyUpdates;
    const isRisksValid = getIsRisksValid(risks);
    const isWorkplansValid = getIsWorkplansValid(workplans);
    const isRisksNotEmpty = getIsListNotEmpty(risks);
    const isWorkplansNotEmpty = getIsListNotEmpty(workplans);
    const isEditValid = isGranularityDateValid && isKeyUpdatesValid && isRisksValid && isWorkplansValid;
    const isEditAvailable = isGranularityDateAvailable || isKeyUpdatesValid || isRisksNotEmpty || isWorkplansNotEmpty;
    const validations = getProjectUpdateFormValidations({
      isDateDataValid: isGranularityDateValid,
      isKeyUpdatesValid,
      isRisksValid,
      isWorkplansValid,
    });
    if (!isGranularityDateValid && isGranularityDateExisted) {
      const dateDataValidationIdx = validations?.findIndex((validation) => validation?.key === 'dateData');
      if (dateDataValidationIdx !== -1 && validations[dateDataValidationIdx]) {
        validations[dateDataValidationIdx].message = 'Duplicate date, please select again!';
      }
    }
    setEditValid?.(isEditValid);
    setEditAvailable?.(isEditAvailable);
    setEditValidations(validations);
  }, [keyUpdatesText, currentList]);

  const [isGRActionItemsLoading, setIsGRActionItemsLoading] = useState(false);

  const refreshGRActionItems = async (
    granularity: 1 | 2 | (number & {}),
    date: string,
    params: {
      project_id: string;
      granularity: '1' | '2' | (string & {});
      start_date: string;
      end_date: string;
    },
  ) => {
    try {
      setIsGRActionItemsLoading(true);
      const res = await fetchGRActionItems(params);
      const { milestone_id: milestoneId, milestone, workplans } = res?.data || {};
      const currentListTmp = cloneDeep(currentList);
      if (currentListTmp) {
        currentListTmp.granularity = granularity || 1;
        currentListTmp.date = date || '';
        currentListTmp.gr_action_milestone_id = milestoneId || '';
        currentListTmp.gr_action_milestone = milestone || '';
        currentListTmp.gr_action = formatGRActionItemsRes(workplans) || [];
        setCurrentList(currentListTmp);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setIsGRActionItemsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ getData }), [getData]);

  // 自动保存hooks
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams,
    saveParamsCur: getData(),
    isBegin: true,
    isChangeValue,
  });

  if (!isReady) return null;

  return (
    <div style={style} className={classnames('dbg-update-project-edit font14', className)}>
      <Form className="gp-form dbg-update-project-edit-form" layout="fixed" fixedLabelWidth={206}>
        <Form.Item
          className="dbg-update-project-edit-form-item dbg-project-update-date-control"
          required
          label="Time Period"
          showStatusIcon={false}
          status={editValidationsMapping?.dateDataFieldValidation === null ? 'success' : 'error'}
          message={editValidationsMapping?.dateDataFieldValidation?.message}
        >
          {/* is-error 错误类名 */}
          <DateSelector
            type={initDataRef?.current?.granularity === 1 ? 'month' : 'week'}
            defaultValue={initDataRef?.current?.date || ''}
            onChange={handleDateUpdate}
          />
        </Form.Item>

        <Form.Item
          className="dbg-update-project-edit-form-item dbg-editor-control"
          required
          label="Key Updates"
          showStatusIcon={false}
          status={editValidationsMapping?.keyUpdatesFieldValidation === null ? 'success' : 'error'}
          message={editValidationsMapping?.keyUpdatesFieldValidation?.message}
        >
          <div className="dbg-wang-editor-box">
            <TinyEditor
              className="project-update-card-key-updates-wang-editor gp-word-break-word"
              placeholder={KEY_UPDATES_PLACEHOLDER}
              status="success"
              value={initDataRef?.current?.content?.key_updates || ''}
              getEditorContent={handleKeyUpdatesUpdate}
              getEditorText={(v: string) => {
                setKeyUpdatesText(v);
              }}
            />
          </div>
        </Form.Item>

        <Form.Item
          className="dbg-update-project-edit-form-item dbg-gr-action-items-control"
          showStatusIcon={false}
          status="success"
          message=""
          label={getGRActionItemsFormItemLabel(grActionItemsMilestone)}
        >
          <GRActionItems
            isShowInitialTips={!currentList?.date}
            isLoading={isGRActionItemsLoading}
            isEmpty={!hasGRActionItems}
            isError={false}
            list={grActionItems}
            onDelete={handleGRActionItemsDelete}
            onChange={handleGRActionItemsUpdate}
            actionItemCategory={actionItemCategory}
          />
        </Form.Item>

        <Form.Item
          className="dbg-update-project-edit-form-item dbg-workplans-control"
          label="Other Action Items"
          showStatusIcon={false}
          status={editValidationsMapping?.workplansFieldValidation === null ? 'success' : 'error'}
          message={editValidationsMapping?.workplansFieldValidation?.message}
        >
          <ActionItems
            isSynchronizing={isWorkplansSynchronizing}
            isEmpty={!hasWorkplansData}
            list={workplansData}
            onAdd={(type) => {
              handleAdd('workplans', type);
            }}
            onDelete={(index) => {
              handleDelete('workplans', index);
            }}
            onSynchronize={handleWorkplansSynchronize}
            onChange={handleWorkplansUpdate}
          />
        </Form.Item>

        <Form.Item
          className="dbg-update-project-edit-form-item dbg-risks-control"
          label="Risks"
          showStatusIcon={false}
          status={editValidationsMapping?.risksFieldValidation === null ? 'success' : 'error'}
          message={editValidationsMapping?.risksFieldValidation?.message}
        >
          <Risks
            isSynchronizing={isRisksSynchronizing}
            isEmpty={!hasRisksData}
            list={risksData}
            onAdd={(type) => {
              handleAdd('risks', type);
            }}
            onDelete={(index) => {
              handleDelete('risks', index);
            }}
            onSynchronize={handleRiskSynchronize}
            onChange={handleRisksUpdate}
            actionItemCategory={actionItemCategory}
          />
        </Form.Item>

        <Form.Item label="Material" showStatusIcon={false} status={'success'} message={''}>
          <UploadFileList
            uniqueId="1"
            uploadFolderName={UPLOAD_FOLDER_NAME_MAP.updatesMaterial}
            defaultList={initialFileDatas}
            // @ts-ignore
            onResult={handleMaterialUpdate}
            // 组件内部是否允许提交的回调
            getIsAllow={(_payload: boolean) => {}}
            setIsCompletedVideo={setIsCompletedVideo}
            setDeletePathList={setDeletePathList}
            deletePathList={deletePathList}
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default React.memo(React.forwardRef(UpdateProjectEditForm));
